import { useEffect, useRef, useState } from "react";
import { ScanLine, X, PackageSearch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useActiveStore } from "@/hooks/use-active-store";
import { useStores } from "@/hooks/use-stores";
import { useStockLookup, StockLookupFailure } from "@/hooks/use-stock-lookup";
import { isolateScannerKeys, useScannerFocusGuard } from "@/hooks/use-scanner-input";
import { playSuccess, playError } from "@/lib/sounds";
import {
  stockView, variationTitle, errorCopy, freshness, needsWarehouseName, locationLabel,
  type StockLookup, type StockLookupVariation, type StockLocation,
} from "@/lib/stock-lookup-view";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type HistoryEntry = { sku: string; ok: boolean; summary: string };

const toneClass: Record<string, string> = {
  strong: "text-foreground",
  danger: "text-destructive",
  muted: "text-muted-foreground",
};

export function StockCheckPage() {
  const { storeId, setStoreId } = useActiveStore();
  const { data: stores } = useStores();
  const lookup = useStockLookup();

  const [sku, setSku] = useState("");
  const [result, setResult] = useState<StockLookup | null>(null);
  const [failure, setFailure] = useState<{ sku: string; title: string; hint: string } | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  // Trgovina koju je promijenio sam scan — da čišćenje rezultata na promjenu trgovine ne
  // obriše upravo ono što je scan našao u drugom shopu.
  const switchedByScan = useRef<string | null>(null);

  // Skener kuca u ovo polje i završava Enterom — fokus se vraća sam, inače njegov Enter
  // aktivira šta god je zadnje bilo fokusirano.
  useScannerFocusGuard(inputRef);
  // Ekran se otvara spreman za skeniranje: prvi fokus ide odmah, pa još jednom poslije prvog
  // frejma (sidebar i store switcher se montiraju iza nas i znaju povući fokus na sebe).
  // Povratak u tab također vraća fokus — skener inače kuca u prazno.
  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    focus();
    const frame = requestAnimationFrame(focus);
    const onVisible = () => { if (document.visibilityState === "visible") focus(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", focus);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", focus);
    };
  }, []);
  useEffect(() => {
    if (switchedByScan.current === storeId) return;
    setResult(null);
    setFailure(null);
  }, [storeId]);

  const storeName = stores?.find((s: any) => s.id === storeId)?.name ?? null;

  async function runLookup(raw: string) {
    const scanned = raw.trim();
    if (!scanned) return;
    setSku("");
    try {
      const data = await lookup.mutateAsync({ sku: scanned, storeId });
      setResult(data);
      setFailure(null);
      playSuccess();
      // Naljepnica ne nosi ime shopa: kad je artikal iz druge trgovine, scan prebacuje na nju
      // umjesto da javi „nema ga". Promjena je vidljiva — u zaglavlju i kroz obavijest.
      if (data.storeId !== storeId) {
        switchedByScan.current = data.storeId;
        setStoreId(data.storeId);
        toast.info(`Prebačeno na ${data.storeName}`);
      }
      const matched = data.variations.find((v) => v.id === data.matchedVariationId);
      const view = stockView(matched ? matched.stock : data.product.stock);
      pushHistory({ sku: scanned, ok: true, summary: view.value });
    } catch (e) {
      const fail = e instanceof StockLookupFailure ? e : null;
      const copy = errorCopy(fail?.code ?? null, fail?.status);
      setResult(null);
      setFailure({ sku: scanned, ...copy });
      playError();
      pushHistory({ sku: scanned, ok: false, summary: copy.title });
    } finally {
      inputRef.current?.focus();
    }
  }

  function pushHistory(entry: HistoryEntry) {
    setHistory((prev) => [entry, ...prev.filter((h) => h.sku !== entry.sku)].slice(0, 8));
  }

  return (
    <div className="flex flex-col h-full">
      {/* Skenersko polje je zakucano na vrh: radnik ga nikad ne traži, ni poslije dugog popisa. */}
      <div className="sticky top-0 z-10 bg-background border-b border-foreground/10">
        <div className="mx-auto w-full max-w-3xl px-4 pt-3 pb-3 flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Provjera stanja
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
              {result?.storeName ?? storeName ?? "Sve trgovine"}
            </span>
          </div>

          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              onKeyDown={isolateScannerKeys((e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void runLookup(e.currentTarget.value);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setSku("");
                  setResult(null);
                  setFailure(null);
                }
              })}
              placeholder="Skeniraj SKU artikla"
              aria-label="SKU artikla"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              className="h-14 pl-11 pr-10 font-mono text-base tracking-wide"
            />
            {sku && (
              <button
                type="button"
                onClick={() => { setSku(""); inputRef.current?.focus(); }}
                aria-label="Očisti polje"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto mx-auto w-full max-w-3xl">
        {lookup.isPending && <Board state="loading" />}
        {!lookup.isPending && failure && <Failure {...failure} />}
        {!lookup.isPending && !failure && result && (
          <Result data={result} />
        )}
        {!lookup.isPending && !failure && !result && <Board state="empty" />}
      </div>

      {history.length > 0 && (
        <div className="mx-auto w-full max-w-3xl border-t border-foreground/10 px-4 py-2 flex gap-2 overflow-x-auto">
          {history.map((h, i) => (
            <button
              key={`${h.sku}-${i}`}
              type="button"
              onClick={() => void runLookup(h.sku)}
              className={cn(
                "shrink-0 px-2 py-1 text-[11px] font-mono ring-1 transition-colors",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground",
                h.ok ? "ring-foreground/15 hover:bg-accent" : "ring-destructive/30 text-destructive hover:bg-destructive/5",
              )}
              title={h.summary}
            >
              {h.sku}
              {h.ok && <span className="ml-2 text-muted-foreground">{h.summary}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Board({ state }: { state: "empty" | "loading" }) {
  return (
    <div className="h-full min-h-64 flex flex-col items-center justify-center gap-3 text-center px-6">
      <PackageSearch className={cn("h-10 w-10 text-foreground/15", state === "loading" && "motion-safe:animate-pulse")} />
      <p className="text-sm text-muted-foreground">
        {state === "loading" ? "Provjeravam stanje…" : "Skeniraj SKU da vidiš stanje artikla."}
      </p>
      {state === "empty" && (
        <p className="text-xs text-muted-foreground/70">
          Skener ili ručni unos, pa Enter. Pregled ne mijenja zalihu.
        </p>
      )}
    </div>
  );
}

function Failure({ sku, title, hint }: { sku: string; title: string; hint: string }) {
  return (
    <div className="px-4 py-8 flex flex-col items-center text-center gap-2">
      <span className="font-mono text-xs text-muted-foreground">{sku}</span>
      <h2 className="text-lg font-medium text-destructive">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-sm">{hint}</p>
    </div>
  );
}

function Result({ data }: { data: StockLookup }) {
  const productView = stockView(data.product.stock);
  const withWarehouse = needsWarehouseName(data);

  return (
    <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150">
      <div className="px-4 py-4 flex gap-3 items-start border-b border-foreground/10">
        <Thumb src={data.product.imageUrl} />
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-medium leading-tight truncate">{data.product.name}</h1>
          <p className="font-mono text-xs text-muted-foreground mt-0.5 truncate">{data.product.sku}</p>
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-1.5">
            {data.product.type === "variable"
              ? `${data.totalVariations} ${plural(data.totalVariations)}`
              : "Jednostavan proizvod"}
            {" · "}
            {freshness(data.fetchedAt)}
          </p>
        </div>
        {data.product.type === "simple" && (
          <Quantity view={productView} inverted={false} />
        )}
      </div>

      {data.product.type === "simple" ? (
        <div className="px-4 py-3 flex flex-col gap-2">
          <Locations locations={data.product.locations} withWarehouse={withWarehouse} inverted={false} showEmpty />
          <p className="text-xs text-muted-foreground">
            {productView.note ?? `Stanje: ${productView.status}`}
          </p>
        </div>
      ) : (
        <ul>
          {data.variations.map((v) => (
            <VariationRow
              key={v.id}
              variation={v}
              matched={v.id === data.matchedVariationId}
              withWarehouse={withWarehouse}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Skenirana varijanta je jedini invertovan red na ekranu — s dva metra se vidi koji je red tvoj. */
function VariationRow({ variation, matched, withWarehouse }: {
  variation: StockLookupVariation;
  matched: boolean;
  withWarehouse: boolean;
}) {
  const view = stockView(variation.stock);
  return (
    <li
      className={cn(
        "flex items-center gap-3 px-4 py-3 border-b border-foreground/10 last:border-0",
        matched && "bg-foreground text-background",
      )}
      aria-current={matched ? "true" : undefined}
    >
      <span className={cn("w-0.5 self-stretch shrink-0", matched ? "bg-background" : "bg-foreground/15")} />
      <div className="min-w-0 flex-1">
        <p className={cn("text-sm leading-tight truncate", matched && "font-medium")}>
          {variationTitle(variation)}
        </p>
        <p className={cn("font-mono text-xs truncate mt-0.5", matched ? "text-background/70" : "text-muted-foreground")}>
          {variation.sku || "bez SKU-a"}
        </p>
        {view.note && (
          <p className={cn("text-[11px] mt-0.5 truncate", matched ? "text-background/70" : "text-muted-foreground")}>
            {view.note}
          </p>
        )}
        {/* Lokacija odgovara na drugo pitanje radnika: ne „koliko", nego „gdje". Prazna lista
            se ispisuje samo na skeniranom redu — inače bi dugačak popis varijanti bio šum. */}
        <Locations
          locations={variation.locations}
          withWarehouse={withWarehouse}
          inverted={matched}
          showEmpty={matched}
        />
      </div>
      <Quantity view={view} inverted={matched} />
    </li>
  );
}

function Locations({ locations, withWarehouse, inverted, showEmpty }: {
  locations: StockLocation[];
  withWarehouse: boolean;
  inverted: boolean;
  showEmpty?: boolean;
}) {
  if (locations.length === 0) {
    if (!showEmpty) return null;
    return (
      <p className={cn("text-[11px] mt-1", inverted ? "text-background/60" : "text-muted-foreground/70")}>
        bez skladišne lokacije
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {locations.map((loc, i) => (
        <span
          key={`${loc.warehouseName}-${loc.binLabel}-${i}`}
          className={cn(
            "font-mono text-[11px] leading-none px-1.5 py-1 ring-1",
            inverted ? "ring-background/40 text-background" : "ring-foreground/15 text-foreground",
          )}
        >
          {locationLabel(loc, withWarehouse)}
        </span>
      ))}
    </div>
  );
}

function Quantity({ view, inverted }: { view: ReturnType<typeof stockView>; inverted: boolean }) {
  return (
    <div className="text-right shrink-0">
      <div
        className={cn(
          "font-mono text-3xl leading-none tabular-nums",
          inverted ? "text-background" : toneClass[view.tone],
        )}
      >
        {view.value}
      </div>
      <div className={cn("text-[10px] uppercase tracking-[0.14em] mt-1", inverted ? "text-background/70" : "text-muted-foreground")}>
        {view.status}
      </div>
    </div>
  );
}

function Thumb({ src }: { src: string | null }) {
  if (!src) {
    return (
      <div className="h-16 w-16 shrink-0 ring-1 ring-foreground/10 flex items-center justify-center">
        <PackageSearch className="h-5 w-5 text-foreground/20" />
      </div>
    );
  }
  return <img src={src} alt="" className="h-16 w-16 shrink-0 object-cover ring-1 ring-foreground/10" />;
}

function plural(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "varijanta";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "varijante";
  return "varijanti";
}
